// Snel door de weken in de Dagstart (wiki-feedback 26 sep: "je wilt hier een scrollbar
// onderaan, dat je veel sneller naar volgende weken kan gaan"). Twee wegen, allebei op het
// bestaande venster (dagShift in werkdagen):
//   · horizontaal scrollen (trackpad, Shift + muiswiel) schuift het venster per werkdag —
//     live tijdens het gebaar (Joris 6 okt: "naar rechts scrollen is veel te traag"); de
//     ophaalronde wacht wel tot het venster stilstaat (zie useDagstartData, VENSTER_RUST_MS);
//   · een weekbalk onderaan springt in één beweging naar een week (zie WeekBalk.jsx).
// Puur waar het kan, zodat het te testen is.
import { useEffect, useRef } from 'react';
import { addDays, formatDateISO, getMonday, parseISODate } from '../../utils/weekUtils';

/** Zoveel pixels horizontaal scrollen = één werkdag verder. */
export const WIEL_DREMPEL = 90;

/**
 * Opgespaarde horizontale scroll omzetten in hele werkdag-stappen.
 * @returns {{ stappen: number, rest: number }}
 */
export function wielStappen(opgespaard, deltaX, drempel = WIEL_DREMPEL) {
  const totaal = (Number(opgespaard) || 0) + (Number(deltaX) || 0);
  const stappen = Math.trunc(totaal / drempel);
  return { stappen, rest: totaal - stappen * drempel };
}

/**
 * Hoe vaak het venster tijdens een scrollgebaar mag verspringen. Eerder wachtte het venster
 * tot het scrollen 220 ms stil viel; met de nadeining van een trackpad gebeurde er dan
 * anderhalve seconde niets en sprong het daarna in één keer. Nu volgt het je vinger, met
 * hooguit één hertekening per tik (geen setState per wiel-event).
 */
export const BUNDEL_MS = 120;

/**
 * Stappen doseren (throttle): de eerste stap gaat meteen door, daarna hooguit één aanroep
 * per `tik` ms met de som van wat er in de tussentijd bij kwam. Heen en terug binnen één tik
 * heft op (geen aanroep).
 * @returns {{ voegToe: (n:number) => void, stop: () => void }}
 */
export function maakBundelaar(onKlaar, tik = BUNDEL_MS, nu = () => Date.now()) {
  let som = 0;
  let timer = null;
  let laatste = -Infinity;
  const leeg = () => {
    timer = null;
    laatste = nu();
    const totaal = som;
    som = 0;
    if (totaal) onKlaar(totaal);
  };
  return {
    voegToe(n) {
      som += n;
      if (!som || timer) return;
      const wacht = laatste + tik - nu();
      if (wacht <= 0) leeg();
      else timer = setTimeout(leeg, wacht);
    },
    stop() { clearTimeout(timer); timer = null; som = 0; },
  };
}

/** Maandag (ISO) van de week `n` weken na de week van `vandaagISO`. */
export function maandagVanWeek(vandaagISO, n) {
  const ma = getMonday(parseISODate(vandaagISO));
  return formatDateISO(addDays(ma, 7 * n));
}

/** Hoeveel weken ligt de week van `datumISO` na die van `vandaagISO` (negatief = eerder). */
export function weekVerschil(vandaagISO, datumISO) {
  const a = getMonday(parseISODate(vandaagISO));
  const b = getMonday(parseISODate(datumISO));
  if (!a || !b) return 0;
  return Math.round((b - a) / (7 * 864e5));
}

/**
 * Kan dit element (of een ouder tot en met `grens`) zelf nog verder horizontaal scrollen?
 * De grens telt mee: in de Dagstart per persoon is die laag zelf de scrollcontainer.
 */
function kanZelfScrollen(doel, grens, richting) {
  let el = doel;
  while (el) {
    if (el.scrollWidth > el.clientWidth + 1) {
      if (richting < 0 ? el.scrollLeft > 0 : el.scrollLeft + el.clientWidth < el.scrollWidth - 1) return true;
    }
    if (el === grens) break;
    el = el.parentElement;
  }
  return false;
}

/**
 * Horizontaal scrollen boven `ref` = het venster per werkdag verschuiven. Een tijdlijn die
 * zelf nog kan scrollen (4 weken breed) krijgt voorrang; pas aan de rand bladert het venster.
 * Niet-passieve listener, zodat de browser niet tegelijk "terug" navigeert (Mac-swipe).
 */
export function useWielBladeren(ref, onStappen) {
  const opgespaard = useRef(0);
  const stilTimer = useRef(null);
  const cb = useRef(onStappen);
  cb.current = onStappen;
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const bundel = maakBundelaar((n) => cb.current(n));
    const opWiel = (e) => {
      const dx = e.shiftKey && !e.deltaX ? e.deltaY : e.deltaX;
      if (!dx || Math.abs(dx) < Math.abs(e.shiftKey ? 0 : e.deltaY)) return;
      if (kanZelfScrollen(e.target, el, Math.sign(dx))) return;
      e.preventDefault();
      const { stappen, rest } = wielStappen(opgespaard.current, dx);
      opgespaard.current = rest;
      clearTimeout(stilTimer.current);
      stilTimer.current = setTimeout(() => { opgespaard.current = 0; }, 250);
      if (stappen) bundel.voegToe(stappen);
    };
    el.addEventListener('wheel', opWiel, { passive: false });
    return () => { el.removeEventListener('wheel', opWiel); clearTimeout(stilTimer.current); bundel.stop(); };
  }, [ref]);
}
